import { Text, Flex, TableContainer, useToast } from '@chakra-ui/react'
import { useForm } from 'react-hook-form'
import { Input } from '../../../components/Form/Input'
import { Button } from '../../../components/Button'
import { api } from '../../../services/api'
import { useMutation } from 'react-query'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import * as T from '../../../components/Table'

const schema = z.object({
  numero_nota: z.string().nonempty('Campo obrigatório'),
})

async function enviarConfirmacaoEstoquePorEmail({ numero_nota }) {
  const res = await api.post('/site/v2/orcamento-crm/envia-confirmacao-estoque', {
    numero_unico: numero_nota,
  })

  return res.data
}

async function onConfirmEstoque(data) {
  const res = await api.get('/site/v2/orcamento-crm/busca-itens-confirmacao-estoque', {
    params: {
      numero_nota: data.numero_nota,
    },
  })

  return res.data
}

async function solicitarPorEmail({ numero_nota }) {
  const res = await api.post('/site/v2/orcamento-crm/solicita-confirmacao-estoque', {
    numero_unico: numero_nota,
  })

  return res.data
}

export function ConfirmaEstoque() {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
  })

  const toast = useToast({
    position: 'top-right',
    duration: 5000,
    isClosable: true,
  })

  const { data, isLoading, mutateAsync } = useMutation({
    mutationFn: onConfirmEstoque,
    onSuccess: () => {},
    onError: (error) => {
      toast({
        title: 'Erro ao confirmar estoque',
        status: 'error',
      })
    },
  })

  const {
    data: dataEmail,
    isLoading: isLoadingEmail,
    mutateAsync: mutateEmail,
  } = useMutation({
    mutationFn: enviarConfirmacaoEstoquePorEmail,
    onSuccess: () => {
      console.log('Email enviado com sucesso')

      toast({
        title: 'Email enviado com sucesso',
        status: 'success',
      })
    },
    onError: (error) => {
      console.error('Erro ao enviar email', error)

      toast({
        title: 'Erro ao enviar email',
        status: 'error',
      })
    },
  })

  const {
    data: dataSolicitarEmail,
    isLoading: isLoadingSolicitarEmail,
    mutateAsync: mutateSolicitarEmail,
  } = useMutation({
    mutationFn: solicitarPorEmail,
    onSuccess: () => {
      console.log('Email solicitado com sucesso')

      toast({
        title: 'Email solicitado com sucesso',
        status: 'success',
      })
    },
    onError: (error) => {
      console.error('Erro ao solicitar email', error)

      toast({
        title: 'Erro ao solicitar email',
        status: 'error',
      })
    },
  })

  async function onSubmit(data) {
    await mutateAsync(data)
  }

  async function enviarEmail({ numero_nota }) {
    await mutateEmail({ numero_nota })
  }

  async function solicitarEmail({ numero_nota }) {
    await mutateSolicitarEmail({ numero_nota })
  }

  const buttonEnviarConfirmacaoDisabled = !data || data.some((item) => !item.status)

  return (
    <>
      <Flex gap={5} width="100%" alignItems="center" justifyContent="center" flexDirection={'column'}>
        <Text
          as="h1"
          size="large"
          fontSize="1.5rem"
          textTransform={'uppercase'}
          textAlign="center"
          marginTop=".4rem"
          padding="1rem"
          width="100%"
          bg="#378BBF"
          color="white"
        >
          Confirmar <strong>Estoque</strong>
        </Text>

        <Flex width="100%" maxW="400px" flexDir="column" gap={2}>
          <Flex gap={2} alignItems="end" justifyContent="center" mt="2.7rem" as="form" onSubmit={handleSubmit(onSubmit)}>
            <Input name="proposta" label="Numero único" {...register('numero_nota')} />
            <Button isLoading={isLoading} size="sm" type="submit" mt="1.5rem" width="100%" color="white" bg="#378BBF">
              Confirmar
            </Button>
          </Flex>
          {errors.numero_nota && (
            <Text color="red" alignSelf="flex-start">
              {errors.numero_nota.message}
            </Text>
          )}
        </Flex>

        <TableContainer maxW="1200px" width="100%" mt="2rem" overflowX="auto">
          <T.Table>
            <T.Thead>
              <T.Tr>
                <T.Th>Numero. Nota</T.Th>
                <T.Th>Numero. Pedido</T.Th>
                <T.Th>Cod. Prod</T.Th>
                <T.Th>Cor</T.Th>
                <T.Th>Quantidade Estoque</T.Th>
                <T.Th>Quantidade Negociada</T.Th>
                <T.Th>Status</T.Th>
              </T.Tr>
            </T.Thead>
            <T.Tbody>
              {data?.map((item) => (
                <T.Tr key={item.id}>
                  <T.Td>{item.numero_nota}</T.Td>
                  <T.Td>{item.numero_pedido}</T.Td>
                  <T.Td>{item.codigo_produto}</T.Td>
                  <T.Td>{item.descricao_cor}</T.Td>
                  <T.Td>{item.quantidade_estoque}</T.Td>
                  <T.Td>{item.quantidade_negociada}</T.Td>
                  <T.Td>
                    <Text color={item.status ? 'green.500' : 'red.500'} fontWeight="bold" textTransform="uppercase">
                      {item.status ? 'Confirmado' : 'Não Confirmado'}
                    </Text>
                  </T.Td>
                </T.Tr>
              ))}
            </T.Tbody>
          </T.Table>

          {data && (
            <Flex gap={2} justifySelf="flex-end" mt={2}>
              <Button
                bgColor="green.500"
                isDisabled={buttonEnviarConfirmacaoDisabled}
                onClick={() => enviarEmail({ numero_nota: data[0].numero_nota })}
                isLoading={isLoadingEmail}
              >
                Enviar Confirmação de Estoque por E-mail
              </Button>

              <Button onClick={() => solicitarEmail({ numero_nota: data[0].numero_nota })} isLoading={isLoadingSolicitarEmail} bgColor="gray.500">
                Solicitar por E-mail
              </Button>
            </Flex>
          )}
        </TableContainer>

        {!data && (
          <Text as="span" textAlign={'center'}>
            Busque por uma proposta existente para confirmar estoque.
          </Text>
        )}
      </Flex>
    </>
  )
}
